/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { ComboBox, IComboBox, IComboBoxOption, IComboBoxProps, IComboBoxStyles } from '@fluentui/react/lib/ComboBox';
import { ISelectableOption } from '@fluentui/react/lib/SelectableOption';
import { Stack } from '@fluentui/react/lib/Stack';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisText } from 'src/core/components/LuisText';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import { Entity } from 'src/luis/projects/entities/types/Entity';
import { EntityRole } from 'src/luis/projects/entities/types/EntityRole';
import { LuisId } from 'src/luis/types/Resource';

const newRoleOptionKey = 'newRoleOptionKey';

const scopedTheme = getScopedTheme('AdvancedOptionsPane');

const messages = defineMessages({
    emptyRole: {
        id: 'luis.intentLabelingSidePane.advancedOptions.selectOrCreateRole.emptyRole',
        defaultMessage: 'Select or create a role'
    },
    creatingRole: {
        id: 'luis.intentLabelingSidePane.advancedOptions.selectOrCreateRole.creatingRole',
        defaultMessage: '{value} (Creating...)'
    },
    role: {
        id: 'luis.intentLabelingSidePane.advancedOptions.selectOrCreateRole.role',
        defaultMessage: 'Role'
    },
    newRole: {
        id: 'luis.intentLabelingSidePane.advancedOptions.selectOrCreateRole.newRole',
        defaultMessage: '(Create new role)'
    }
});

const comboboxStyles = classNamesFunction<IComboBoxProps, IComboBoxStyles>()({
    label: { textTransform: 'uppercase', fontSize: '10px', color: scopedTheme.titleTextColor }
});

export type SelectOrCreateEntityRoleProps = {
    isProgressing?: boolean;
    entity: Entity;
    selectedRole: EntityRole;
    onCreateRole: (entity: Entity, role: string) => void;
    onSelectRole: (entity: Entity, roleId: LuisId) => void;
};

export const SelectOrCreateEntityRole = (props: SelectOrCreateEntityRoleProps) => {
    const { isProgressing, entity, onSelectRole, onCreateRole, selectedRole } = props;
    const { roles } = entity;

    const { localize } = useLocalization();
    const [newRoleName, setNewRoleName] = React.useState('');

    let options = React.useMemo(
        () => roles.map(({ id, name: roleName }: EntityRole) => ({
                key: id,
                text: roleName,
                ariaLabel: roleName
            })),
        [roles]
    );

    // If new role name doesn't exist show the item, otherwise don't show it.
    if (newRoleName && !options.find(o => o.text === newRoleName)) {
        // Update if it's there, if not add it.
        if (options.length && options[options.length - 1].key === newRoleOptionKey) {
            options[options.length - 1] = {
                ...options[options.length - 1],
                text: newRoleName,
                ariaLabel: `${newRoleName} ${localize(messages.newRole)}`
            };
        } else {
            options.push({ key: newRoleOptionKey, text: newRoleName, ariaLabel: `${newRoleName} ${localize(messages.newRole)}` });
        }
    } else {
        options = options.filter(o => o.key !== newRoleOptionKey);
    }

    const handleChange = (_event: React.FormEvent<IComboBox>, option: IComboBoxOption) => {
        if (option) {
            if (option.key === newRoleOptionKey) {
                onCreateRole(entity, newRoleName);
            } else {
                onSelectRole(entity, option.key);
            }
        } else if (newRoleName) {
            const foundOption = options.filter(o => o.key !== newRoleOptionKey).find(o => o.text === newRoleName);
            if (foundOption) {
                onSelectRole(entity, foundOption.key);
            } else {
                onCreateRole(entity, newRoleName);
            }
        }
    };

    const handlePendingValueChange = (option: IComboBoxOption, _: number, value: string) => {
        if (!option && value !== undefined) {
            setNewRoleName(value);
        }
    };

    const renderOption = (optionProps: ISelectableOption, defaultRender: (optionProps: ISelectableOption) => JSX.Element) => {
        const { key } = optionProps;
        const option = defaultRender(optionProps);

        return key !== newRoleOptionKey ? (
            option
        ) : (
            <Stack horizontal tokens={{ childrenGap: 8 }}>
                {option}
                <LuisText style={{ fontStyle: 'italic' }}>{localize(messages.newRole)}</LuisText>
            </Stack>
        );
    };

    return (
        <ComboBox
            allowFreeform
            options={options}
            openOnKeyboardFocus
            disabled={isProgressing}
            styles={comboboxStyles}
            useComboBoxAsMenuWidth
            onChange={handleChange}
            onRenderOption={renderOption}
            selectedKey={selectedRole?.id}
            label={localize(messages.role)}
            calloutProps={{ calloutMaxHeight: 300 }}
            onPendingValueChanged={handlePendingValueChange}
            text={isProgressing ? localize(messages.creatingRole, { value: newRoleName }) : selectedRole?.name}
            placeholder={isProgressing ? localize(messages.creatingRole, { value: newRoleName }) : localize(messages.emptyRole)}
        />
    );
};
